/**
 * AddChapter — Create a new life chapter.
 *
 * Fields: title, start year, end year (optional), starter prompt (optional).
 * Accessible, senior-friendly: large inputs, clear labels, minimal fields.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ArrowLeft, Calendar, Feather } from 'lucide-react';
import { createChapter, getMyLifeData } from '../services/chaptersAPI';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";

const currentYear = new Date().getFullYear();

export default function AddChapter() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endYear, setEndYear] = useState('');
  const [starterPrompt, setStarterPrompt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = title.trim() && startYear && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const start = parseInt(startYear, 10);
    const end = endYear ? parseInt(endYear, 10) : null;

    if (start < 1900 || start > currentYear) {
      setError('Please enter a valid start year.');
      return;
    }
    if (end && (end < start || end > currentYear + 10)) {
      setError('End year must be after the start year.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Auto-assign chapter number (next after current max)
      const lifeData = await getMyLifeData();
      const chapters = lifeData.chapters || [];
      const maxNumber = chapters.reduce((max, ch) => Math.max(max, ch.number || 0), 0);

      await createChapter({
        title: title.trim(),
        number: maxNumber + 1,
        start_year: start,
        end_year: end,
        starter_prompt: starterPrompt.trim() || undefined,
      });
      navigate('/my-life');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || 'Something went wrong. Please try again.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setSaving(false);
    }
  };

  return (
    <div style={{
      maxWidth: 600, margin: '0 auto', padding: '24px 16px',
      fontFamily: FONT_B, color: '#2C2F26', minHeight: '100vh',
      background: '#FAFAF5',
    }}>
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', color: '#556B2F',
          fontSize: 15, cursor: 'pointer', padding: '8px 0',
          marginBottom: 16, fontFamily: FONT_B,
        }}
      >
        <ArrowLeft size={18} /> Back
      </button>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <BookOpen size={36} color="#556B2F" style={{ marginBottom: 8 }} />
        <h1 style={{
          fontFamily: FONT_H, fontSize: 28, fontWeight: 700,
          color: '#2C2F26', margin: '0 0 6px',
        }}>
          Add a New Chapter
        </h1>
        <p style={{ color: '#7C846A', fontSize: 16, margin: 0 }}>
          Create a chapter for a period of your life
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Title */}
        <div style={{ marginBottom: 24 }}>
          <label style={{
            display: 'block', fontSize: 15, fontWeight: 600,
            color: '#3A4A20', marginBottom: 6,
          }}>
            Chapter Title *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Growing Up in the Village, College Years, Starting a Family"
            maxLength={200}
            style={{
              width: '100%', padding: '14px 16px', fontSize: 18,
              border: '1.5px solid #D4D1C4', borderRadius: 12,
              fontFamily: FONT_B, color: '#2C2F26', background: '#fff',
              outline: 'none', minHeight: 52, boxSizing: 'border-box',
            }}
            onFocus={(e) => e.target.style.borderColor = '#556B2F'}
            onBlur={(e) => e.target.style.borderColor = '#D4D1C4'}
          />
        </div>

        {/* Year range */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          <div style={{ flex: 1 }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 15, fontWeight: 600, color: '#3A4A20', marginBottom: 6,
            }}>
              <Calendar size={16} /> Start Year *
            </label>
            <input
              type="number"
              value={startYear}
              onChange={(e) => setStartYear(e.target.value)}
              placeholder="e.g. 1985"
              min={1900}
              max={currentYear}
              style={{
                width: '100%', padding: '14px 16px', fontSize: 18,
                border: '1.5px solid #D4D1C4', borderRadius: 12,
                fontFamily: FONT_B, color: '#2C2F26', background: '#fff',
                outline: 'none', minHeight: 52, boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#556B2F'}
              onBlur={(e) => e.target.style.borderColor = '#D4D1C4'}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 15, fontWeight: 600, color: '#3A4A20', marginBottom: 6,
            }}>
              <Calendar size={16} /> End Year
            </label>
            <input
              type="number"
              value={endYear}
              onChange={(e) => setEndYear(e.target.value)}
              placeholder="Leave blank for present"
              min={1900}
              max={currentYear + 10}
              style={{
                width: '100%', padding: '14px 16px', fontSize: 18,
                border: '1.5px solid #D4D1C4', borderRadius: 12,
                fontFamily: FONT_B, color: '#2C2F26', background: '#fff',
                outline: 'none', minHeight: 52, boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#556B2F'}
              onBlur={(e) => e.target.style.borderColor = '#D4D1C4'}
            />
            <p style={{ fontSize: 13, color: '#9AA088', marginTop: 4, marginBottom: 0 }}>
              Leave blank if this chapter is still ongoing
            </p>
          </div>
        </div>

        {/* Starter prompt (optional) */}
        <div style={{ marginBottom: 32 }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 15, fontWeight: 600, color: '#3A4A20', marginBottom: 6,
          }}>
            <Feather size={16} /> Writing Prompt
            <span style={{ fontWeight: 400, color: '#9AA088' }}>(optional)</span>
          </label>
          <textarea
            value={starterPrompt}
            onChange={(e) => setStarterPrompt(e.target.value)}
            placeholder="A question to help you start writing about this chapter, e.g. 'What is your earliest memory from this time?'"
            maxLength={500}
            rows={3}
            style={{
              width: '100%', padding: '14px 16px', fontSize: 16,
              border: '1.5px solid #D4D1C4', borderRadius: 12,
              fontFamily: FONT_B, color: '#2C2F26', background: '#fff',
              outline: 'none', resize: 'vertical', minHeight: 80,
              boxSizing: 'border-box',
            }}
            onFocus={(e) => e.target.style.borderColor = '#556B2F'}
            onBlur={(e) => e.target.style.borderColor = '#D4D1C4'}
          />
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: '#FFF0F0', borderRadius: 10, padding: '12px 16px',
            marginBottom: 16, color: '#8B0000', fontSize: 15,
          }}>
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            width: '100%', padding: '16px', fontSize: 18, fontWeight: 700,
            background: canSubmit ? '#556B2F' : '#D4D1C4',
            color: canSubmit ? '#fff' : '#A7A996',
            border: 'none', borderRadius: 14, cursor: canSubmit ? 'pointer' : 'not-allowed',
            fontFamily: FONT_B, minHeight: 56,
            transition: 'background 0.15s',
          }}
        >
          {saving ? 'Creating...' : 'Create Chapter'}
        </button>
      </form>
    </div>
  );
}
